import api from '@api/apiServices';
import useDefaultInfiniteQuery from '@hooks/useDefaultInfiniteQuery';
import { Autocomplete, AutocompleteProps, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@uidotdev/usehooks';
import { omitBy } from 'lodash';
import { useMemo, useState } from 'react';
import {
    Control,
    Controller,
    FieldValues,
    Path,
    useWatch,
} from 'react-hook-form';
import InfiniteScroll from './InfiniteScroll';

interface AsyncAutocompleteProps<T extends FieldValues>
    extends AutocompleteProps<T, boolean, boolean, boolean> {
    control: Control<T>;
    label?: string;
    name: Path<T>;
    additinalParams?: Record<string, any>;
}

const FieldSearchAutocomplete = <T extends FieldValues>({
    control,
    label,
    name,
    additinalParams,
}: AsyncAutocompleteProps<T>) => {
    const [isFocused, setIsFocused] = useState(false);

    const value = useWatch({
        control,
        name,
    });

    const debouncedValue = useDebounce(value, 400);

    const params = useMemo(
        () =>
            omitBy(
                { ...additinalParams, [`${name}__icontains`]: debouncedValue },
                value => value === '',
            ),
        [debouncedValue, isFocused],
    );

    const optionsQuery = useDefaultInfiniteQuery({
        queryKey: ['fieldSearch', name, params],
        queryFn: ({ pageParam: page }) =>
            api.contacts['field-search'][name].get({ ...params, page }),
        enabled: isFocused,
        placeholderData: (_, previousQuery) => previousQuery?.state?.data,
    });

    const options = optionsQuery?.data?.flatData;

    return (
        <Controller
            name={name}
            control={control}
            render={({
                field: { onChange, ...rest },
                fieldState: { error },
            }) => (
                <Autocomplete
                    fullWidth
                    freeSolo
                    options={options || []}
                    disableClearable
                    onChange={(event, newValue) => onChange(newValue)}
                    onInputChange={(event, newValue) => onChange(newValue)}
                    // open
                    ListboxComponent={InfiniteScroll}
                    ListboxProps={{
                        loadMore: optionsQuery.fetchNextPage,
                        hasMore: optionsQuery.hasNextPage,
                        // loading: optionsQuery.isFetchingNextPage,
                        ListComponent: 'ul',
                    }}
                    {...rest}
                    renderInput={params => (
                        <TextField
                            {...params}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                            variant="filled"
                            size="small"
                            label={label}
                            error={Boolean(error)}
                            helperText={error?.message}
                        />
                    )}
                />
            )}
        />
    );
};

export default FieldSearchAutocomplete;
